import SharedActionAccordion from '../../../../shared/shared-action-accordion';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  GroupHeader,
  GroupItems,
} from '../../../../shared/autocomplete-groups-ui';
import { Policy } from '../../../../../types';
import { RulesEngineFormValues } from '../rules-engine-form-page';
import { policyActions } from '../../../../shared/policy-actions';
import { useSearchParams } from 'react-router-dom';
import { transformActionHash } from '../../../policies/policy-utils';
import { policyNarratives } from '../../../policies/policy-review-narratives';
import { PolicyAction } from '../../../policies/policy-dialog';
import GeneralDialog from '../../../../shared/general-dialog';
import { useState } from 'react';
import { Messages } from '../../../../utils/message-constants';
import { pagesConfig } from '../../../../shared/pages-config';
import ActionDescription from './action-description';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import RemoveRedEyeOutlined from '@mui/icons-material/RemoveRedEyeOutlined';

interface RunPolicyActionProps {
  description?: string;
  control: Control<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  allPolicies: Policy[];
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  onCreatePolicy: () => void;
  canCreatePolicy: boolean;
}

function formatSourceType(sourceType: string): string {
  switch (sourceType) {
    case 'All_Users':
      return 'All Users';
    case 'All_Groups':
      return 'All Groups';
    case 'OrgUnit':
      return 'Org Unit';
    case 'gPanel_Role':
      return 'gPanel Role';
    case 'Mobile_Device':
      return 'Mobile Device';
    default:
      return sourceType;
  }
}

export default function RunPolicyAction({
  control,
  errors,
  allPolicies,
  clearErrors,
  setValue,
  onCreatePolicy,
  canCreatePolicy,
  description,
}: RunPolicyActionProps) {
  const theme = useTheme();
  const [, setSearchParams] = useSearchParams();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  return (
    <Stack gap={2}>
      <ActionDescription description={description} />
      <GeneralDialog
        open={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        title="Leave to Create Policy?"
        dialogContent={
          <Typography variant="body1">
            To create a new policy, we&apos;ll need to take you to the Policy
            Creator. Your current rule will be saved as a draft.
          </Typography>
        }
        primaryButton={{
          title: 'SAVE & GO TO POLICY CREATION',
          action: () => {
            setConfirmDialogOpen(false);
            onCreatePolicy();
          },
          loading: false,
          disabled: false,
        }}
        secondaryButton={{
          title: 'CANCEL',
          action: () => setConfirmDialogOpen(false),
          loading: false,
          disabled: false,
        }}
      />
      <Controller
        name="policyInfo.policyId"
        control={control}
        render={({ field }) => {
          const selectedPolicy =
            field.value !== 0
              ? allPolicies.find((p) => p.id === field.value) ?? null
              : null;

          return (
            <>
              <Autocomplete
                autoHighlight
                autoComplete
                options={[...allPolicies].sort((a, b) => {
                  if (a.policyType === b.policyType) {
                    return (a.policyName ?? '').localeCompare(
                      b.policyName ?? ''
                    );
                  }
                  return (a.policyType ?? '').localeCompare(b.policyType ?? '');
                })}
                getOptionLabel={(option) => option.policyName ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Action"
                    error={!!errors.policyInfo?.policyId}
                    helperText={errors?.policyInfo?.policyId?.message || ''}
                  />
                )}
                groupBy={(option) => option.policyType ?? 'standard'}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>
                      {params.group === 'standard'
                        ? 'Standard'
                        : 'New User Creation'}
                    </GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                fullWidth
                value={
                  field.value === 0
                    ? null
                    : allPolicies.find((option) => option.id === field.value) ??
                      null
                }
                onChange={(_, data) => {
                  if (data) {
                    field.onChange(data.id);
                    setValue('policyInfo.policyType', data.policyType);
                    clearErrors('policyInfo.policyId');
                  } else {
                    field.onChange(0);
                    setValue('policyInfo.policyType', '');
                  }
                }}
              />

              {selectedPolicy && (
                <Box
                  sx={{
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? theme.palette.background.paper
                        : theme.palette.slateBlue[50],
                    borderRadius: 2,
                    p: 2,
                    ...(theme.palette.mode === 'dark' && {
                      border: '1px solid',
                      borderColor: theme.palette.slateBlue[500],
                    }),
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                    <RemoveRedEyeOutlined
                      sx={{
                        color:
                          theme.palette.mode === 'dark'
                            ? theme.palette.slateBlue[300]
                            : theme.palette.slateBlue[500],
                        width: 20,
                        height: 20,
                      }}
                    />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Policy Preview
                    </Typography>
                  </Stack>

                  <Stack gap={0.5} mb={1.5}>
                    <Stack direction="row" gap={1}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 100 }}
                      >
                        Policy Type
                      </Typography>
                      <Typography variant="body2">
                        {selectedPolicy.policyType === 'standard'
                          ? 'Standard'
                          : 'New User Creation'}
                      </Typography>
                    </Stack>

                    {selectedPolicy.policyType !== 'newUser' && (
                      <>
                        <Stack direction="row" gap={1}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 100 }}
                          >
                            Target Type
                          </Typography>
                          <Typography variant="body2">
                            {formatSourceType(selectedPolicy.sourceType)}
                          </Typography>
                        </Stack>

                        <Stack direction="row" gap={1} alignItems="flex-start">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 100 }}
                          >
                            Targets
                          </Typography>
                          <Typography variant="body2">
                            {selectedPolicy.allSource
                              ? 'All'
                              : selectedPolicy.sources?.length > 0
                              ? selectedPolicy.sources.slice(0, 2).join(', ') +
                                (selectedPolicy.sources.length > 2
                                  ? ` +${
                                      selectedPolicy.sources.length - 2
                                    } more`
                                  : '')
                              : '—'}
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Stack>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Actions:
                  </Typography>

                  <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                    <Stack gap={1}>
                      {(() => {
                        const actionData = selectedPolicy.actionHash
                          ? transformActionHash(selectedPolicy.actionHash)
                          : [];
                        return selectedPolicy.actions?.map((actionType) => {
                          const normalizedType = actionType.replaceAll(
                            ' ',
                            '_'
                          );
                          const actionMeta = policyActions.find(
                            (a) => a.value === normalizedType
                          );
                          const actionInstance = actionData.find(
                            (a) => a.type === normalizedType
                          );
                          const narrativeFn = policyNarratives[normalizedType];
                          const narrative = narrativeFn
                            ? typeof narrativeFn === 'function'
                              ? actionInstance
                                ? narrativeFn(
                                    actionInstance as PolicyAction,
                                    selectedPolicy
                                  )
                                : actionMeta?.description
                              : narrativeFn
                            : actionMeta?.description;
                          return (
                            <SharedActionAccordion
                              key={actionType}
                              id={normalizedType}
                              title={actionMeta?.label ?? actionType}
                              type={actionMeta?.groupType}
                              description={narrative}
                              expanded={true}
                              onToggleExpand={() => {}}
                            />
                          );
                        });
                      })()}
                    </Stack>
                  </Box>

                  <Stack alignItems="flex-end" mt={0.5}>
                    <Button
                      variant="text"
                      size="small"
                      color="primary"
                      type="button"
                      onClick={() =>
                        setSearchParams((prev) => {
                          prev.set(
                            'selectedPolicy',
                            selectedPolicy.id.toString()
                          );
                          return prev;
                        })
                      }
                      sx={{
                        textDecoration: 'underline',
                        textTransform: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      View Policy Details
                    </Button>
                  </Stack>
                </Box>
              )}

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                <Typography variant="body2" color="text.secondary">
                  Missing a policy?
                </Typography>
                <Tooltip
                  arrow
                  title={
                    !canCreatePolicy
                      ? Messages.Errors.Permission.Acting('Create Policy')
                      : ''
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!canCreatePolicy}
                      onClick={() => setConfirmDialogOpen(true)}
                      type="button"
                    >
                      {pagesConfig.createPolicy.name}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </>
          );
        }}
      />
    </Stack>
  );
}
